import React from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  MutationAcceptSellerOrderArgs,
  SellerOrder as SellerOrderType,
  SellerOrderConnection,
  SellerOrderStatus,
} from '@shopify-multiseller/graphql/partner/types';
import { AdminUI } from '../../ui/AdminUI';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';
import { Button, Input, SegmentedControl } from '../../components/primitives';
import { SectionCard } from '../../components/SectionCard';
import { EmptyState } from '../../components/EmptyState';
import { SellerOrderStatusBadge } from '../../components/StatusBadge/SellerOrderStatusBadge';
import { useNotification } from '../../components';
import { Spinner } from '../../components';
import { useSettings, useShopCurrency } from '../../hooks';
import { expiryClause } from '../../helpers/acceptWindow';
import { formatCents, formatDate } from '../../helpers/formatters';
import { cn } from '../../lib/utils';
import { GET_ORDER_QUERY, ACCEPT_ORDER_MUTATION } from '../SellerOrder/queries';
import { OrderItems } from '../SellerOrder/OrderItems';
import { AcceptSellerOrderModal } from '../SellerOrder/AcceptSellerOrderModal';
import { RejectSellerOrderModal } from '../SellerOrder/RejectSellerOrderModal';
import { ORDER_STATUS_COUNT_QUERY } from '../SellerOrders/queries';
import { TRIAGE_ORDERS_QUERY } from './queries';

/**
 * The two queues, and only the two queues.
 *
 * The spike draws a third segment, "All". Review took it apart from two
 * directions at once — "the segmented selector only really works when there's
 * two items perhaps, there's something that doesn't look right about that",
 * and "is the orders list view redundant, given that we've got the order
 * triage view" — and the two questions answer each other. "All" is not a
 * queue: it puts fulfilled, rejected and cancelled orders into a pane whose
 * whole purpose is the next decision, and in doing so it makes this screen a
 * second, worse orders list. Dropping it gives the two screens one job each.
 * Everything is still one click away — the list is linked from the pane head.
 */
type Queue = 'accept' | 'fulfil';

const QUEUE_STATUS: Record<Queue, SellerOrderStatus[]> = {
  accept: [SellerOrderStatus.Pending],
  fulfil: [SellerOrderStatus.PendingFulfillment],
};

type ListData = { sellerOrderConnection: SellerOrderConnection };
type CountData = { sellerOrderConnection: { count: number } };

const customerName = (order: { customer?: { firstName?: string | null; lastName?: string | null } | null }) =>
  [order.customer?.firstName, order.customer?.lastName].filter(Boolean).join(' ');

/** Products plus the shipping the customer paid — the total on their receipt. */
const paidByCustomer = (order: SellerOrderType): number =>
  order.total + (order.items ?? []).reduce((sum, item) => sum + (item.shippingPrice ?? 0), 0);

type Sort = 'expiring' | 'oldest' | 'newest';

const SORTS: { value: Sort; label: string }[] = [
  { value: 'expiring', label: 'Expiring soon' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'newest', label: 'Newest first' },
];

/**
 * The order the queue is worked in.
 *
 * Sorted here rather than by the server: `sellerOrderConnection` takes no sort
 * argument, so this orders the page it has. That is honest for a queue — the
 * pane holds the whole of a queue anyone is realistically clearing — but it is
 * the page, not the table, and a seller with more than a page of pending
 * orders is being shown the earliest of those loaded, not of all of them.
 *
 * "Expiring soon" and "Oldest first" are the same ordering while every order
 * shares one accept window, which they do; they are kept apart because the
 * window is a shop setting and the labels say different things.
 */
const sortRows = (rows: SellerOrderType[], sort: Sort): SellerOrderType[] =>
  [...rows].sort((a, b) => {
    const left = new Date(a.orderDate).getTime();
    const right = new Date(b.orderDate).getTime();
    return sort === 'newest' ? right - left : left - right;
  });

/**
 * A queue you work through, rather than a list you keep coming back to.
 *
 * The default orders screen is a full-page list: accepting three morning
 * orders means three round trips out to the list and back. Here the list keeps
 * its place in the left pane while the order being decided fills the right, so
 * the work is a sequence of decisions instead of a sequence of navigations.
 *
 * Deliberately a separate route rather than the default orders view — this is
 * reached when there is a queue to clear, and the full list remains the place
 * to go looking for a particular order.
 */
export const OrderTriage: React.FC = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const currencyCode = useShopCurrency();
  const acceptWindowHours = useSettings().sellerOrderAccept?.orderAcceptTimeout ?? 0;
  const expiresIn = (orderDate: string) => expiryClause(orderDate, acceptWindowHours);
  const [searchParams, setSearchParams] = useSearchParams();

  const queueParam = searchParams.get('queue');
  const queue: Queue = queueParam === 'fulfil' ? 'fulfil' : 'accept';
  const selectedId = searchParams.get('order') ?? undefined;
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<Sort>('expiring');
  const [updating, setUpdating] = React.useState(false);
  const [showAccept, setShowAccept] = React.useState(false);
  const [showReject, setShowReject] = React.useState(false);
  const [order, setOrder] = React.useState<SellerOrderType | undefined>(undefined);

  const { data: listData, loading: listLoading, refetch } = useQuery<ListData>(TRIAGE_ORDERS_QUERY, {
    variables: { first: 25, filter: search, sellerOrderStatus: QUEUE_STATUS[queue] },
  });
  const { data: pendingCount } = useQuery<CountData>(ORDER_STATUS_COUNT_QUERY, {
    variables: { sellerOrderStatus: [SellerOrderStatus.Pending] },
  });
  const { data: fulfilCount } = useQuery<CountData>(ORDER_STATUS_COUNT_QUERY, {
    variables: { sellerOrderStatus: [SellerOrderStatus.PendingFulfillment] },
  });

  const [getOrder, { loading: orderLoading }] = useLazyQuery<{ sellerOrder: SellerOrderType }>(
    GET_ORDER_QUERY,
  );
  const [acceptSellerOrder] = useMutation<
    { acceptSellerOrder: SellerOrderType },
    MutationAcceptSellerOrderArgs
  >(ACCEPT_ORDER_MUTATION);

  /*
    Orders decided in this sitting. The list is served from the search index,
    which is populated asynchronously from the write — so the single refetch
    fired after a decision can land before the index has caught up and return
    the order still pending. There is nothing to trigger another fetch, so the
    order a seller just accepted sat in the queue until they navigated away.
    Holding the decided ids here takes it out of the queue the moment the
    decision succeeds; the refetch then confirms it.
  */
  const [decidedIds, setDecidedIds] = React.useState<string[]>([]);

  // The connection's node is typed as the Node interface; every edge here is a
  // seller order by construction of the query.
  const rows: SellerOrderType[] = (listData?.sellerOrderConnection.edges ?? [])
    .map((edge) => edge?.node as SellerOrderType | undefined)
    .filter((node): node is SellerOrderType => !!node)
    .filter((node) => !decidedIds.includes(node.id));
  const rowsInOrder = sortRows(rows, sort);

  // Land on the first order in the queue rather than an empty right pane —
  // the point of the screen is that the next decision is already in front of
  // you.
  React.useEffect(() => {
    if (!selectedId && rowsInOrder.length > 0) {
      // The first row *as shown*, which is the whole point of the sort: the
      // most urgent order is the one already open.
      setSearchParams({ queue, order: rowsInOrder[0]!.id }, { replace: true });
    }
  }, [selectedId, rowsInOrder.length, queue, sort]);

  React.useEffect(() => {
    if (!selectedId) {
      setOrder(undefined);
      return;
    }
    getOrder({ variables: { id: selectedId } }).then((result) => {
      setOrder(result.data?.sellerOrder ?? undefined);
    });
  }, [selectedId]);

  const select = (id: string) => setSearchParams({ queue, order: id });
  // Switching queue asks a different question, so decisions made against the
  // previous one stop applying.
  const selectQueue = (next: Queue) => {
    setDecidedIds([]);
    setSearchParams({ queue: next });
  };

  const decide = (accepted: boolean) => {
    if (!order) return;
    setUpdating(true);
    setShowAccept(false);
    setShowReject(false);
    acceptSellerOrder({ variables: { id: order.id, accepted } })
      .then(() => {
        notification.success({
          message: accepted ? 'Order accepted' : 'Order rejected',
          description: `Order #${order.idAlias}`,
        });
        setUpdating(false);
        // Clear the decided order out of the queue and move to the next one.
        setDecidedIds((current) => [...current, order.id]);
        setSearchParams({ queue });
        refetch();
      })
      .catch(() => {
        setUpdating(false);
        notification.error({
          message: 'Failed',
          description: `Order #${order.idAlias} could not be updated.`,
        });
      });
  };

  const awaiting = pendingCount?.sellerOrderConnection.count ?? 0;

  return (
    <AdminUI>
      <>
      <SaveBarBlocker>
        <div data-testid="order-triage" className="min-h-screen">
          {/*
            One flat tinted strip across the top of the screen, per the spike.
            This was a bordered Alert card sitting inside a bordered white
            band — a panel inside a panel, drawing three horizontal rules in
            the space of forty pixels for a single sentence of context.
          */}
          {awaiting > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border bg-amber-50 px-6 py-2.5 text-xs text-amber-800">
              {/* The sentence the design writes, as one run and with the
                  reason in it: a count of orders says something is waiting,
                  "accepting within 72 hours keeps them from expiring" says
                  what happens if it keeps waiting. The window is the shop's
                  own setting rather than a number written into the copy. */}
              <span>
                <b className="font-semibold">Review queue</b>
                {` · ${awaiting} ${awaiting === 1 ? 'order' : 'orders'} awaiting acceptance${
                  acceptWindowHours > 0
                    ? ` — accepting within ${acceptWindowHours} hours keeps them from expiring`
                    : ''
                }`}
              </span>
              <Link to="/orders" className="ml-auto font-medium underline underline-offset-2">
                Exit queue
              </Link>
            </div>
          )}

          <div className="grid min-h-[calc(100vh-4rem)] grid-cols-1 lg:grid-cols-[22rem_1fr]">
            <aside className="flex flex-col border-b border-border bg-card lg:border-b-0 lg:border-r">
              <div className="space-y-2.5 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <SegmentedControl<Queue>
                    aria-label="Queue"
                    size="sm"
                    value={queue}
                    onChange={selectQueue}
                    options={[
                      {
                        // "To accept", not "Pending". Review asked the
                        // question the old label invited — "is it pending
                        // fulfilment, is it pending acceptance?" — because
                        // both segments named a thing that was pending and
                        // only one of them said what for. Two queues, one
                        // grammar: what you have to do to each.
                        value: 'accept',
                        label: `To accept · ${pendingCount?.sellerOrderConnection.count ?? 0}`,
                        testId: 'triage-queue-pending',
                      },
                      {
                        value: 'fulfil',
                        label: `To fulfil · ${fulfilCount?.sellerOrderConnection.count ?? 0}`,
                        testId: 'triage-queue-fulfil',
                      },
                    ]}
                  />
                  {/* The way back to everything else, in the pane rather than
                      in the banner above it: the banner only appears when
                      orders are waiting to be accepted, so a seller working
                      the fulfilment queue with nothing pending had no exit. */}
                  <Link
                    to="/orders"
                    data-testid="triage-all-orders"
                    className="ml-auto text-xs text-primary hover:underline"
                  >
                    All orders
                  </Link>
                </div>
                <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  {/* A pane control, sized like one — the full-height field
                      made the head of a 22rem list pane as tall as a form. */}
                  <Input
                    aria-label="Search"
                    data-testid="triage-search"
                    placeholder="Search # or customer…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                </div>
                {/* Which end of the queue to start from. A queue with no
                    stated order is one you have to read all of before you
                    can pick the next thing to do. */}
                <select
                  aria-label="Sort"
                  data-testid="triage-sort"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as Sort)}
                  className="h-8 max-w-[7.5rem] rounded-md border border-input bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {SORTS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                </div>
              </div>

              <div data-testid="triage-list">
                {listLoading && (
                  <div className="px-4 py-6">
                    <Spinner size="small" />
                  </div>
                )}
                {!listLoading && rowsInOrder.length === 0 && (
                  <EmptyState
                    size="sm"
                    icon={Search}
                    title="Nothing in this queue"
                    description="Orders appear here when they need a decision from you."
                  />
                )}
                {!listLoading &&
                  rowsInOrder.map((row) => {
                    // A row is an order, so it counts what is in the order.
                    // It used to lead with the first item's picture and title
                    // — "#1092 · Aurora Table Lamp ×2 +1" — which read as a
                    // row about a product that happened to carry an order
                    // number. Review: "the left side makes no sense as it
                    // lists specific items, whereas an order can contain
                    // multiple items."
                    const lines = row.items?.length ?? 0;
                    const units = (row.items ?? []).reduce(
                      (sum, item) => sum + (item?.quantity ?? 0),
                      0,
                    );
                    return (
                      <button
                        key={row.id}
                        type="button"
                        data-testid="triage-list-item"
                        aria-current={row.id === selectedId ? 'true' : undefined}
                        onClick={() => select(row.id)}
                        className={cn(
                          // Selected reads as selected: a tint plus a rule down
                          // the inner edge, rather than a flat grey fill that
                          // was hard to tell from the hover state.
                          'flex w-full items-center gap-2.5 border-b border-border/60 border-r-2 border-r-transparent px-4 py-2.5 text-left transition-colors',
                          row.id === selectedId
                            ? 'border-r-primary bg-primary/5'
                            : 'hover:bg-muted/50',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block truncate text-[13px] font-medium',
                              row.id === selectedId ? 'text-primary' : 'text-foreground',
                            )}
                          >
                            {`#${row.idAlias} · ${customerName(row) || 'Customer'}`}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {[
                              // What is in it, as a count of both things a
                              // seller packs by: how many different products,
                              // and how many units in total.
                              `${lines} ${lines === 1 ? 'product' : 'products'} · ${units} ${
                                units === 1 ? 'item' : 'items'
                              }`,
                              // What the customer paid, shipping included —
                              // the figure on their receipt. The products
                              // total is on the order itself, where it is
                              // being broken down anyway.
                              formatCents({ amount: paidByCustomer(row), currencyCode }),
                              expiresIn(row.orderDate) ??
                                formatDate(row.orderDate, { month: 'short', day: 'numeric' }),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </button>
                    );
                  })}
              </div>

              {/* The bottom of the pane says how much queue is left, so the
                  count doesn't only live in the strip at the very top of the
                  page — by the time you have scrolled the list it is gone. */}
              {rowsInOrder.length > 0 && (
                <div className="mt-auto border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
                  {`${rowsInOrder.length} ${rowsInOrder.length === 1 ? 'order' : 'orders'} in this queue`}
                </div>
              )}
            </aside>

            <section className="min-w-0 px-6 py-5">
              {orderLoading && !order && <Spinner size="small" />}
              {!orderLoading && !order && (
                <EmptyState
                  icon={Search}
                  title="Nothing selected"
                  description="Choose an order from the queue to see it here."
                />
              )}
              {order && (
                <>
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <h1 className="text-xl font-semibold tracking-tight text-foreground">
                        {`Order #${order.idAlias}`}
                      </h1>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {`Placed ${formatDate(order.orderDate, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                        })}` +
                          (expiresIn(order.orderDate) ? ` · ${expiresIn(order.orderDate)}` : '')}
                      </p>
                    </div>
                    {/* Default size, so an unaccepted order reads "Pending"
                        here. The long form belongs on the order's own page,
                        where there is a line of room for it; in a split pane
                        it wraps the header. */}
                    <SellerOrderStatusBadge status={order.status} />
                    {order.status === SellerOrderStatus.Pending && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          data-testid="triage-reject-button"
                          disabled={updating}
                          onClick={() => setShowReject(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          data-testid="triage-accept-button"
                          disabled={updating}
                          loading={updating}
                          onClick={() => setShowAccept(true)}
                        >
                          Accept order
                        </Button>
                      </div>
                    )}
                    {order.status === SellerOrderStatus.PendingFulfillment && (
                      <Button
                        variant="primary"
                        data-testid="triage-fulfill-button"
                        onClick={() => navigate(`/orders/${order.id}/fulfill`)}
                      >
                        Fulfill
                      </Button>
                    )}
                  </div>

                  <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
                    <OrderItems
                      items={order.items}
                      currencyCode={currencyCode}
                      showTotal={order.status === SellerOrderStatus.Pending}
                    />
                    <div className="space-y-5">
                      <SectionCard
                        // "Payout", not "If you accept": the card sits under an
                        // Accept/Reject pair on an order nobody has answered
                        // yet, so the condition is already on the screen. The
                        // total line still says the figure is an estimate.
                        title="Payout"
                        testId="triage-payout-estimate"
                      >
                        <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2">
                          <span className="text-sm text-muted-foreground">Products total</span>
                          <span className="text-sm font-medium tabular-nums text-foreground">
                            {formatCents({ amount: order.total, currencyCode })}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2">
                          <span className="text-sm text-muted-foreground">Fees &amp; commission</span>
                          <span className="text-sm font-medium tabular-nums text-foreground">
                            {`−${formatCents({ amount: order.totalFees, currencyCode })}`}
                          </span>
                        </div>
                        <div className="mt-1 flex items-baseline justify-between gap-4 border-t border-border pt-3">
                          <span className="text-sm font-semibold text-foreground">
                            {order.status === SellerOrderStatus.Pending
                              ? 'Estimated payout'
                              : "You'll receive"}
                          </span>
                          <span className="text-lg font-semibold tabular-nums text-foreground">
                            {formatCents({ amount: order.totalPayout, currencyCode })}
                          </span>
                        </div>
                      </SectionCard>

                      <SectionCard title="Customer" testId="triage-customer">
                        <p className="text-sm font-medium text-foreground">{customerName(order)}</p>
                        {order.customer?.shippingAddress && (
                          /* Two lines, not four: a flat number is part of the
                             street and the country belongs with the town, which
                             is how the design sets it and how the order page
                             already did. */
                          <p className="mt-1 text-sm text-muted-foreground">
                            {[
                              order.customer.shippingAddress.address1,
                              order.customer.shippingAddress.address2,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                            <br />
                            {[
                              [
                                order.customer.shippingAddress.city,
                                order.customer.shippingAddress.provinceCode,
                                order.customer.shippingAddress.zip,
                              ]
                                .filter(Boolean)
                                .join(' '),
                              order.customer.shippingAddress.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                        <Link
                          to={`/orders/${order.id}`}
                          className="mt-3 inline-block text-sm text-primary hover:underline"
                        >
                          Open full order →
                        </Link>
                      </SectionCard>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </SaveBarBlocker>

      <AcceptSellerOrderModal
        open={showAccept}
        onClose={() => setShowAccept(false)}
        onAccept={() => decide(true)}
        updating={updating}
      />
      <RejectSellerOrderModal
        open={showReject}
        onClose={() => setShowReject(false)}
        onReject={() => decide(false)}
        updating={updating}
      />
      </>
    </AdminUI>
  );
};
